import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { render, BookOptions } from '../../pdf/book';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;

export class TestBookFloating implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const markdown = `
# Floating Objects Test

This document demonstrates floating objects with text wrapping.

## Left Float

::: float=left
This is a left-floating content block. It should appear on the left side of the page with text flowing around it on the right.
:::

This is regular paragraph text that should flow around the floating element. The floating element takes up about 40% of the width by default. Text should wrap around the floated content on the right side. This allows for creating magazine-style layouts where images or other content are placed to the side of regular text.

## Right Float

::: float=right
This is a right-floating content block. It should appear on the right side of the page with text flowing around it on the left.
:::

This paragraph should have a right-floating element to its left. The text flows around the floated content on the left side. This is useful for pull quotes, images, or other content that you want to highlight on the side of the main text flow.

## Float with Custom Width

::: float=left width=50
This is a 50% width left float. It takes up half of the available line width.
:::

This text demonstrates a wider left float that takes 50% of the page width. The remaining text flows on the right side. This can be used for larger images or side-by-side content layouts.

## Mixed Content with Floats

Text before the float.

::: float=right width=30
**Float Content**

This float has custom width and can contain various formatting including **bold**, *italic*, and even inline LaTeX like $$\\frac{a}{b}$$.
:::

Text after the float continues here. The float is positioned on the right side with 30% width.

## Float with Module Content

::: float=left
<dot>
digraph {
  A -> B
  B -> C
}
</dot>
:::

This demonstrates using a float to wrap a DOT graph. The graph floats to the left and text flows around it on the right side.

# Summary

The floating system supports:
- **Left float** - Content aligned to the left
- **Right float** - Content aligned to the right  
- **Width** - Custom width as percentage (e.g., width=50)
- **Content** - Can contain any block element (paragraphs, modules, etc.)

Use the \`:::\` fence syntax to create floating blocks:
- \`\`\`markdown
::: float=left
Your floated content
:::\`\`\`
`;

    const options: BookOptions = {
      fontSize: 11,
      lineHeight: 1.5,
      margin: 20,
      columns: 1,
      defaultAlignment: 'left',
    };

    await render(pdf, markdown, options);

    // Draw layout rectangle bounds for debugging
    const margin = 20;
    const pageWidth = PdfSize.A4.width;
    const pageHeight = PdfSize.A4.height;
    const contentWidth = pageWidth - 2 * margin;
    const contentHeight = pageHeight - 2 * margin;

    const rectPen = new PdfPen(PdfColor.fromRgb(255, 0, 0), 0.5);

    for (const page of pdf.gs) {
      const contentRect = new PdfRect(margin, margin, contentWidth, contentHeight);
      page.drawRectangle(rectPen, null, contentRect);
    }
  }
}
